import { Liquid } from "liquidjs";
import moment from "moment";

export const TYPE_LABEL = { text: "Text", html: "HTML editor", url: "URL / Link", system: "System variable" };
export const SUB_LABEL = {
  custom: "Custom URL",
  itinerary_download: "Itinerary download",
  deeplink: "App deeplink (open app)",
  useful: "Useful",
  not_useful: "Not Useful",
  file: "File (upload / library)",
};
export const SYSTEM_LABEL = { first_name: "First name", last_name: "Last name" };
export const LIMIT_BYTES = 10 * 1024;

export const emptyVariableState = (info) => {
  const saved = { ...(info.saved || {}) };
  if (info.types.length === 1) saved.type = info.types[0]; // e.g. a preheader-only variable is always Text
  return {
    key: info.key,
    type: saved.type || "",
    url_sub_type: saved.url_sub_type || info.url_sub_types[0] || "custom",
    system_variable: saved.system_variable || "first_name",
    value: "",
    file: null, // { id, original_name, url, mime_type }
    mappings: saved.url_sub_type === "itinerary_download" ? [{ users: [], itinerary: null }] : [],
    remembered: !!info.saved,
  };
};

const stripTags = (html) => (html || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();

// Same rules as the backend (app/services/custom_email.py): a value counts as empty when
// there is nothing to send for it.
export const isEmpty = (v) => {
  if (!v || !v.type) return true;
  if (v.type === "text" || v.type === "html") return !stripTags(v.value) && !(v.value || "").includes("<img");
  if (v.type === "url") {
    if (v.url_sub_type === "custom") return !(v.value || "").trim();
    if (v.url_sub_type === "file") return !v.file;
  }
  return false;
};

export const mustFill = (info, varsByKey) => {
  if (info.rule === "required") return true;
  if (info.rule === "conditional") return info.required_when.some((k) => !isEmpty(varsByKey[k]));
  return false;
};

export const itinKeys = (template, varsByKey) =>
  (template?.variables || [])
    .map((i) => varsByKey[i.key])
    .filter((v) => v && v.type === "url" && v.url_sub_type === "itinerary_download")
    .map((v) => v.key);

const refNumbers = (text) => {
  const out = new Set();
  for (const m of (text || "").matchAll(/itinerary:\/\/(\d+)|\{itinerary_(\d+)\}/g)) out.add(+(m[1] || m[2]));
  return out;
};

// itinerary_download variables every recipient must have an itinerary in: required ones,
// and ones referenced by number from Text / HTML ({itinerary_N} or itinerary://N).
export const neededItinKeys = (template, varsByKey) => {
  const keys = itinKeys(template, varsByKey);
  const need = new Set();
  const info = Object.fromEntries((template?.variables || []).map((i) => [i.key, i]));
  keys.forEach((k) => { if (!info[k]?.optional) need.add(k); });
  Object.values(varsByKey).forEach((v) => {
    if (v.type === "text" || v.type === "html") refNumbers(v.value).forEach((n) => keys[n - 1] && need.add(keys[n - 1]));
  });
  return need;
};

const SERIF = ["Playfair-Display", "Merriweather", "Georgia"];
const MONO = ["Courier-New", "Roboto-Mono", "Fira-Code", "Source-Code-Pro"];

// Email apps don't have Quill's CSS, so the classes the editor writes become inline styles:
// ql-font-X -> font-family (with a safe fallback), ql-size-N -> font-size, ql-align-* -> text-align,
// ql-indent-N -> padding-left. Colours, bold, lists, headings ... are already plain HTML / inline.
export const toEmailHtml = (html) => {
  if (!html || !html.includes("ql-")) return html || "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  doc.body.querySelectorAll('[class*="ql-"]').forEach((el) => {
    const styles = [];
    [...el.classList].forEach((c) => {
      let m;
      if ((m = c.match(/^ql-font-(.+)$/))) {
        const fallback = SERIF.includes(m[1]) ? "Georgia, serif" : MONO.includes(m[1]) ? "'Courier New', monospace" : "Arial, sans-serif";
        styles.push(`font-family: '${m[1].replace(/-/g, " ")}', ${fallback}`);
      } else if ((m = c.match(/^ql-size-(\d+)$/))) styles.push(`font-size: ${m[1]}px`);
      else if ((m = c.match(/^ql-align-(\w+)$/))) styles.push(`text-align: ${m[1]}`);
      else if ((m = c.match(/^ql-indent-(\d+)$/))) styles.push(`padding-left: ${Number(m[1]) * 3}em`);
      else return;
      el.classList.remove(c);
    });
    if (styles.length) el.setAttribute("style", [el.getAttribute("style"), ...styles].filter(Boolean).join("; "));
    if (!el.classList.length) el.removeAttribute("class");
  });
  return doc.body.innerHTML;
};

export const toPayloadVariables = (template, varsByKey) =>
  (template?.variables || []).map((info) => {
    const v = varsByKey[info.key];
    if (!v || !v.type) return { key: info.key, type: null };
    return {
      key: info.key,
      type: v.type,
      url_sub_type: v.type === "url" ? v.url_sub_type : null,
      system_variable: v.type === "system" ? v.system_variable : null,
      value: v.type === "html" ? toEmailHtml(v.value) : v.type === "text" || (v.type === "url" && v.url_sub_type === "custom") ? v.value : null,
      file_id: v.type === "url" && v.url_sub_type === "file" ? v.file?.id || null : null,
      mappings:
        v.type === "url" && v.url_sub_type === "itinerary_download"
          ? v.mappings.map((row) => ({ user_ids: row.users.map((u) => u.value), user_itinerary_id: row.itinerary?.value || null }))
          : [],
    };
  });

const engine = new Liquid({ strictVariables: false, strictFilters: false });

// Renders a OneSignal template the way OneSignal's Liquid does. Values OneSignal fills
// itself (first_name, [unsubscribe_url] ...) show as placeholders.
export const renderTemplate = (tpl, customData, email = "") => {
  if (!tpl) return "";
  try {
    return engine
      .parseAndRenderSync(tpl, {
        message: { custom_data: customData || {}, id: "‹message.id›" },
        email,
        first_name: "‹first_name›",
        url: "#",
        button_link: "#",
        unsubscribe_url: "#",
      })
      .replace(/\[unsubscribe_url\]/g, "#");
  } catch (e) {
    return `<pre style="color:#b91c1c;white-space:pre-wrap">Template error: ${String(e.message).replace(/</g, "&lt;")}</pre>`;
  }
};

// Wraps rendered email HTML for a sandboxed iframe: links can't navigate anywhere.
// (Injected inside <head> when the template has one, so its <!doctype> still sets standards mode.)
export const iframeDoc = (html) => {
  const extra = `<base target="_blank"><style>a{cursor:default}html,body{margin:0}</style>`;
  return /<head[^>]*>/i.test(html || "") ? html.replace(/<head[^>]*>/i, (m) => m + extra) : extra + (html || "");
};

const HIDDEN_STYLE_RE = /display\s*:\s*none|max-height\s*:\s*0|opacity\s*:\s*0|font-size\s*:\s*0/i;

// Some templates put the preheader in a hidden <div> at the top of the body instead of using
// OneSignal's own Preheader field (the technique every inbox app itself relies on). When the
// template has no OneSignal preheader, fall back to that hidden block so the preview matches
// what recipients actually see next to the subject line.
export const bodyPreheader = (renderedHtml) => {
  const doc = new DOMParser().parseFromString(renderedHtml || "", "text/html");
  const hidden = [...doc.body.querySelectorAll("[style]")].find((el) => HIDDEN_STYLE_RE.test(el.getAttribute("style")));
  return hidden ? hidden.textContent.replace(/\s+/g, " ").trim() : "";
};

// Dates come from the API in UTC; shown in the admin's local time.
export const fmtDay = (t) => (t ? moment.utc(t).local().format("DD MMM YYYY") : "–");
export const fmtTime = (t) => (t ? moment.utc(t).local().format("DD MMM YYYY, HH:mm") : "–");
